import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Popover,
  makeStyles,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  OpenInNew as OpenIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  InfoOutlined as InfoIcon,
} from "@material-ui/icons";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import {
  getScenarioDetails,
  updateScenario,
} from "../../viewServices/calls/apiCalls";
import {
  getScenarioStatusIcon,
  getScenarioStatusText,
} from "../../viewServices/constants/statusConstants";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../connections/auth/AuthProvider";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";

// Helper function to format created at timestamp with duration
const formatCreatedAtWithDuration = (startTimestamp, endTimestamp) => {
  if (!startTimestamp) {
    return " - ";
  }

  const start = new Date(startTimestamp);
  const formattedDate = start
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "");

  if (!endTimestamp) {
    return formattedDate;
  }

  const end = new Date(endTimestamp);
  const diffMs = end.getTime() - start.getTime();

  if (diffMs < 0) {
    return formattedDate;
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  const remainingSeconds = diffSeconds % 60;
  const remainingMinutes = diffMinutes % 60;
  const remainingHours = diffHours % 24;

  let durationParts = [];
  if (remainingHours > 0) {
    durationParts.push(`${remainingHours}h`);
  }
  if (remainingMinutes > 0) {
    durationParts.push(`${remainingMinutes}m`);
  }
  if (durationParts.length === 0 || remainingSeconds > 0) {
    durationParts.push(`${remainingSeconds}s`);
  }

  const duration = durationParts.join(" ");
  return `${formattedDate} (${duration})`;
};

const DEFAULT_WIDTH = 470; // 30% bigger than original 360px
const MIN_WIDTH = 350;
const MAX_WIDTH = 700;

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    drawer: {
      flexShrink: 0,
    },
    drawerPaper: {
      position: "absolute",
      right: 0,
      top: 0,
      height: "100%",
      borderRadius: 0,
      boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.palette.background.paper,
      overflow: "visible",
    },
    resizeHandle: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      cursor: "ew-resize",
      backgroundColor: "transparent",
      transition: "background-color 0.2s",
      zIndex: 1,
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      },
    },
    resizeHandleActive: {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2, 2.5),
      borderBottom: "1px solid #e0e0e0",
      backgroundColor: "#fafafa",
      flexShrink: 0,
    },
    title: {
      fontSize: "1rem",
      fontWeight: 500,
      maxWidth: 280,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: theme.palette.secondary.main,
    },
    closeButton: {
      color: theme.palette.grey[500],
    },
    content: {
      padding: theme.spacing(2),
      overflowY: "auto",
      flex: 1,
    },
    section: {
      marginBottom: theme.spacing(2.5),
    },
    label: {
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
      textTransform: "uppercase",
      marginBottom: theme.spacing(0.5),
      fontWeight: 500,
    },
    labelWithEdit: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    value: {
      fontSize: "0.9rem",
      color: theme.palette.text.primary,
    },
    statusValue: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontSize: "0.9rem",
    },
    editIconButton: {
      padding: 4,
      color: "#757575",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        color: "#616161",
      },
    },
    editInput: {
      fontSize: "0.9rem",
      padding: theme.spacing(1),
      border: "1px solid #e0e0e0",
      borderRadius: theme.shape.borderRadius,
      width: "100%",
      fontFamily: "inherit",
      backgroundColor: "#fafafa",
      "&:focus": {
        outline: "2px solid #9e9e9e",
        outlineOffset: "1px",
        backgroundColor: "#ffffff",
        borderColor: "#bdbdbd",
      },
    },
    editActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
    },
    actions: {
      padding: theme.spacing(2),
      borderTop: "1px solid #e0e0e0",
      backgroundColor: "#fafafa",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    dangerButton: {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.main,
    },
    variantsSection: {
      marginTop: theme.spacing(2),
    },
    variantsTable: {
      "& .MuiPaper-elevation1": {
        boxShadow: "none",
      },
      "& .MuiTableCell-root": {
        padding: theme.spacing(0.75, 1),
        fontSize: "0.8rem",
      },
      "& .MuiTableHead-root .MuiTableCell-root": {
        backgroundColor: "#f5f5f5",
        fontWeight: 500,
        fontSize: "0.75rem",
      },
    },
    variantNameContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    infoIcon: {
      padding: 2,
      "& svg": {
        fontSize: "1rem",
      },
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(4),
    },
    popover: {
      padding: theme.spacing(2),
      maxWidth: 300,
    },
    infoRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    infoColumn: {
      flex: 1,
    },
  }));

const ScenarioDetailsDrawer = ({
  open,
  scenario,
  folders,
  onClose,
  onFolderChange,
  onVisibilityChange,
  onArchive,
  onRestore,
  onDelete,
  onOpen,
  onRefresh,
  clientColor,
}) => {
  const classes = useStyles(clientColor)();
  const { t } = useTranslation("translation", { keyPrefix: "rgm.cockpit" });
  const { t: tManager } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenario_manager.details_drawer",
  });
  const { userDetails } = useContext(AuthContext);

  // Check if current user owns this scenario
  const currentUsername = userDetails?.username;
  const isOwner = scenario?.created_by === currentUsername;

  const [scenarioDetails, setScenarioDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");

  // Info popover state
  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);
  const [selectedVariantInfo, setSelectedVariantInfo] = useState(null);

  // Resize state
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  // Handle resize drag
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      // Calculate new width based on mouse position relative to right edge
      const containerRect = resizeRef.current
        ?.closest(".MuiDialog-paper")
        ?.getBoundingClientRect();
      if (!containerRect) return;

      const newWidth = containerRect.right - e.clientX;
      const clampedWidth = Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH);
      setDrawerWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  // Fetch detailed scenario data when scenario changes
  useEffect(() => {
    const fetchDetails = async () => {
      if (scenario?.scenario_id) {
        setDetailsLoading(true);
        try {
          const fetchedDetails = await getScenarioDetails(scenario.scenario_id);
          setScenarioDetails(fetchedDetails);
        } catch (err) {
          console.error("Failed to fetch scenario details:", err);
          // Fall back to basic scenario data
          setScenarioDetails(scenario);
        } finally {
          setDetailsLoading(false);
        }
      } else {
        setScenarioDetails(null);
      }
    };
    fetchDetails();
  }, [scenario?.scenario_id]);

  // Reset editing state when drawer closes
  useEffect(() => {
    if (!open) {
      setIsEditingName(false);
      setIsEditingDescription(false);
      setEditedName("");
      setEditedDescription("");
    }
  }, [open]);

  if (!scenario) return null;

  const isArchived = scenario.is_archived;
  const details = scenarioDetails || scenario;

  // Flatten folders for dropdown
  const flattenFolders = (foldersArray, depth = 0) => {
    let result = [];
    for (const folder of foldersArray) {
      result.push({ ...folder, depth });
      if (folder.subfolders?.length > 0) {
        result = result.concat(flattenFolders(folder.subfolders, depth + 1));
      }
    }
    return result;
  };

  const flatFolders = flattenFolders(folders || []);

  // Edit handlers
  const handleStartEditName = () => {
    setEditedName(details.scenario_name || "");
    setIsEditingName(true);
  };

  const handleSaveEditName = async () => {
    if (!editedName.trim()) {
      toast.error(tManager("toast.name_empty"));
      return;
    }
    try {
      await updateScenario(scenario.scenario_id, {
        scenario_name: editedName.trim(),
      });
      setScenarioDetails((prev) => ({
        ...prev,
        scenario_name: editedName.trim(),
      }));
      setIsEditingName(false);
      toast.success(tManager("toast.name_updated"));
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to update scenario name:", err);
      toast.error(tManager("toast.name_update_failed"));
    }
  };

  const handleCancelEditName = () => {
    setEditedName("");
    setIsEditingName(false);
  };

  const handleStartEditDescription = () => {
    setEditedDescription(details.scenario_description || "");
    setIsEditingDescription(true);
  };

  const handleSaveEditDescription = async () => {
    try {
      await updateScenario(scenario.scenario_id, {
        scenario_description: editedDescription.trim(),
      });
      setScenarioDetails((prev) => ({
        ...prev,
        scenario_description: editedDescription.trim(),
      }));
      setIsEditingDescription(false);
      toast.success(tManager("toast.description_updated"));
    } catch (err) {
      console.error("Failed to update description:", err);
      toast.error(tManager("toast.description_update_failed"));
    }
  };

  const handleCancelEditDescription = () => {
    setEditedDescription("");
    setIsEditingDescription(false);
  };

  // Info popover handlers
  const handleInfoClick = (event, variant) => {
    setInfoPopoverAnchor(event.currentTarget);
    setSelectedVariantInfo(variant);
  };

  const handleInfoClose = () => {
    setInfoPopoverAnchor(null);
    setSelectedVariantInfo(null);
  };

  // Group variants by group_level2
  const groupedVariants = details.variants?.reduce((acc, variant) => {
    const groupKey = variant.group_level2 || "Other";
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(variant);
    return acc;
  }, {});

  return (
    <Drawer
      ref={resizeRef}
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      classes={{ paper: classes.drawerPaper }}
      PaperProps={{
        style: { width: drawerWidth },
      }}
    >
      {/* Resize Handle */}
      <div
        className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ""}`}
        onMouseDown={handleMouseDown}
      />

      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.title}>
          {t("scenario_details_modal.title")}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        {detailsLoading ? (
          <Box className={classes.loadingContainer}>
            <ThreeDots
              height="40"
              width="40"
              radius="9"
              color={clientColor || "#1976d2"}
              ariaLabel="loading"
              visible={true}
            />
          </Box>
        ) : (
          <>
            {/* Scenario Name */}
            <Box className={classes.section}>
              <Box className={classes.labelWithEdit}>
                <Typography className={classes.label}>
                  {t("scenario_details_modal.scenario_name")}
                </Typography>
                {!isEditingName && (
                  <IconButton
                    size="small"
                    onClick={handleStartEditName}
                    className={classes.editIconButton}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {isEditingName ? (
                <>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className={classes.editInput}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveEditName();
                      if (e.key === "Escape") handleCancelEditName();
                    }}
                  />
                  <Box className={classes.editActions}>
                    <IconButton
                      size="small"
                      onClick={handleSaveEditName}
                      className={classes.editIconButton}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEditName}
                      className={classes.editIconButton}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Typography className={classes.value}>
                  {details.scenario_name || "-"}
                </Typography>
              )}
            </Box>

            {/* Status and Created By - side by side */}
            <Box className={classes.infoRow}>
              <Box className={classes.infoColumn}>
                <Typography className={classes.label}>
                  {t("scenario_details_modal.status")}
                </Typography>
                <Box className={classes.statusValue}>
                  {getScenarioStatusIcon(details.status)}
                  <Typography variant="body2">
                    {getScenarioStatusText(details.status, t)}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.infoColumn}>
                <Typography className={classes.label}>
                  {t("scenario_details_modal.created_by")}
                </Typography>
                <Typography className={classes.value}>
                  {details.created_by_user || "-"}
                </Typography>
              </Box>
            </Box>

            {/* Description */}
            <Box className={classes.section}>
              <Box className={classes.labelWithEdit}>
                <Typography className={classes.label}>
                  {t("scenario_details_modal.description")}
                </Typography>
                {!isEditingDescription && (
                  <IconButton
                    size="small"
                    onClick={handleStartEditDescription}
                    className={classes.editIconButton}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {isEditingDescription ? (
                <>
                  <input
                    type="text"
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    className={classes.editInput}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveEditDescription();
                      if (e.key === "Escape") handleCancelEditDescription();
                    }}
                  />
                  <Box className={classes.editActions}>
                    <IconButton
                      size="small"
                      onClick={handleSaveEditDescription}
                      className={classes.editIconButton}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEditDescription}
                      className={classes.editIconButton}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Typography className={classes.value}>
                  {details.scenario_description || "-"}
                </Typography>
              )}
            </Box>

            {/* Method and Created At - side by side */}
            <Box className={classes.infoRow}>
              <Box className={classes.infoColumn}>
                <Typography className={classes.label}>
                  {t("scenario_details_modal.calculation_method")}
                </Typography>
                <Typography className={classes.value}>
                  {details.calculation_method || "-"}
                </Typography>
              </Box>
              <Box className={classes.infoColumn}>
                <Typography className={classes.label}>
                  {t("scenario_details_modal.created_at")}
                </Typography>
                <Typography
                  className={classes.value}
                  style={{ fontSize: "0.8rem" }}
                >
                  {formatCreatedAtWithDuration(
                    details.start_timestamp,
                    details.end_timestamp,
                  )}
                </Typography>
              </Box>
            </Box>

            <Divider style={{ margin: "16px 0" }} />

            {/* Folder assignment */}
            <Box className={classes.section}>
              <Typography className={classes.label}>
                {tManager("folder_label")}
              </Typography>
              <FormControl
                size="small"
                className={classes.selectFormControl}
                style={{ marginBottom: 0 }}
              >
                <Select
                  value={scenario.folder_id || ""}
                  onChange={(e) =>
                    onFolderChange(scenario.scenario_id, e.target.value || null)
                  }
                  displayEmpty
                  disabled={isArchived}
                  MenuProps={{
                    className: classes.selectMenu,
                    classes: { paper: classes.selectPaper },
                  }}
                >
                  <MenuItem
                    value=""
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    <em>{tManager("ungrouped")}</em>
                  </MenuItem>
                  {flatFolders.map((folder) => (
                    <MenuItem
                      key={folder.folder_id}
                      value={folder.folder_id}
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {"—".repeat(folder.depth)} {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Visibility - only editable by scenario owner */}
            <Box className={classes.section}>
              <Typography className={classes.label}>
                {tManager("visibility_label")}
              </Typography>
              <FormControl
                size="small"
                className={classes.selectFormControl}
                style={{ marginBottom: 0 }}
              >
                <Select
                  value={scenario.visibility || "shared"}
                  onChange={(e) =>
                    onVisibilityChange(scenario.scenario_id, e.target.value)
                  }
                  disabled={!isOwner}
                  MenuProps={{
                    className: classes.selectMenu,
                    classes: { paper: classes.selectPaper },
                  }}
                >
                  <MenuItem
                    value="shared"
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    {tManager("visibility_shared")}
                  </MenuItem>
                  <MenuItem
                    value="private"
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    {tManager("visibility_private")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Variants Table */}
            {groupedVariants && Object.keys(groupedVariants).length > 0 && (
              <Box className={classes.variantsSection}>
                <Typography
                  className={classes.label}
                  style={{ marginBottom: 8 }}
                >
                  {t("scenario_details_modal.variants") || "Variants"}
                </Typography>
                <TableContainer
                  component={Paper}
                  className={classes.variantsTable}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t("scenario_details_modal.table_group")}
                        </TableCell>
                        <TableCell>
                          {t("scenario_details_modal.table_name")}
                        </TableCell>
                        <TableCell>
                          {t("scenario_details_modal.variant_name")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(groupedVariants).map(
                        ([groupName, variantsInGroup]) =>
                          variantsInGroup.map((variant, idx) => (
                            <TableRow key={`${groupName}-${idx}`}>
                              {idx === 0 && (
                                <TableCell rowSpan={variantsInGroup.length}>
                                  {groupName}
                                </TableCell>
                              )}
                              <TableCell>
                                {variant.table_description ||
                                  variant.table_name}
                              </TableCell>
                              <TableCell>
                                <Box className={classes.variantNameContainer}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleInfoClick(e, variant)}
                                    className={classes.infoIcon}
                                  >
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                  {variant.variant_name}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Actions */}
      <Box className={classes.actions}>
        <Button
          variant="contained"
          startIcon={<OpenIcon />}
          onClick={() => onOpen(scenario.scenario_id)}
          fullWidth
          style={{ backgroundColor: clientColor || "#1976d2", color: "#fff" }}
        >
          {tManager("open_in_board")}
        </Button>

        {isArchived ? (
          <Button
            variant="outlined"
            startIcon={<UnarchiveIcon />}
            onClick={() => onRestore(scenario.scenario_id)}
            fullWidth
          >
            {tManager("restore_from_archive")}
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<ArchiveIcon />}
            onClick={() => onArchive(scenario.scenario_id)}
            fullWidth
          >
            {tManager("move_to_archive")}
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(scenario.scenario_id)}
          fullWidth
          className={classes.dangerButton}
        >
          {tManager("delete_scenario")}
        </Button>
      </Box>

      {/* Variant Info Popover */}
      <Popover
        open={Boolean(infoPopoverAnchor)}
        anchorEl={infoPopoverAnchor}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box className={classes.popover}>
          {selectedVariantInfo && (
            <>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: "bold", marginBottom: 8 }}
              >
                {tManager("variant_info_title")}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                <strong>{tManager("file_name")}</strong>{" "}
                {selectedVariantInfo.file_name || tManager("no_file_name")}
              </Typography>
              <Typography variant="body2">
                <strong>{tManager("uploaded_at")}</strong>{" "}
                {selectedVariantInfo.uploaded_at_timestamp
                  ? new Date(
                      selectedVariantInfo.uploaded_at_timestamp,
                    ).toLocaleString()
                  : tManager("not_available")}
              </Typography>
            </>
          )}
        </Box>
      </Popover>
    </Drawer>
  );
};

ScenarioDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  scenario: PropTypes.object,
  folders: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onFolderChange: PropTypes.func,
  onVisibilityChange: PropTypes.func,
  onArchive: PropTypes.func,
  onRestore: PropTypes.func,
  onDelete: PropTypes.func,
  onOpen: PropTypes.func,
  onRefresh: PropTypes.func,
  clientColor: PropTypes.string,
};

export default ScenarioDetailsDrawer;
